/*!
   Copyright 2019 Ron Buckton

   Licensed under the Apache License, Version 2.0 (the "License");
   you may not use this file except in compliance with the License.
   You may obtain a copy of the License at

       http://www.apache.org/licenses/LICENSE-2.0

   Unless required by applicable law or agreed to in writing, software
   distributed under the License is distributed on an "AS IS" BASIS,
   WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
   See the License for the specific language governing permissions and
   limitations under the License.
*/
import { R } from 'retemplate';
import { getCalendarDateFromWeekDate, getCalendarDateFromOrdinalDate, getWeeksInYear } from './internal/date';
import { PARTIAL_ZONED_CALENDAR_DATETIME_EXTENDED, PARTIAL_ZONED_CALENDAR_DATETIME_BASIC, PARTIAL_ZONED_WEEK_DATETIME_EXTENDED, PARTIAL_ZONED_WEEK_DATETIME_BASIC, PARTIAL_ZONED_ORDINAL_DATETIME_EXTENDED, PARTIAL_ZONED_ORDINAL_DATETIME_BASIC } from './internal/patterns';
import { D6, D4, D3, D2 } from './internal/formats';
import { Temporal } from './types';
import { ZoneOffset } from './Zone';
import { CivilDate } from './CivilDate';
import { CivilTime } from './CivilTime';
import { CivilDateTime } from './CivilDateTime';
import { OffsetTime } from './OffsetTime';
import { OffsetDateTime } from './OffsetDateTime';
import { ZonedDateTime } from './ZonedDateTime';
import { Duration } from './Duration';
import { Equatable, Equaler, StructuralEquatable } from '@esfx/equatable';
import { INTERVAL_HASH_SEED } from './internal/core';

const intervalRegExp = /^(?<first>(?:[^/]|\[[^/]+\])+)\/(?<second>(?:[^/]|\[[^/]+\])+)$/;
const partialCalendarDateTimeExtendedRegExp = new RegExp(R`^(?!$)${PARTIAL_ZONED_CALENDAR_DATETIME_EXTENDED}$`);
const partialCalendarDateTimeBasicRegExp = new RegExp(R`^(?!$)${PARTIAL_ZONED_CALENDAR_DATETIME_BASIC}$`);
const partialWeekDateTimeExtendedRegExp = new RegExp(R`^(?!$)${PARTIAL_ZONED_WEEK_DATETIME_EXTENDED}$`);
const partialWeekDateTimeBasicRegExp = new RegExp(R`^(?!$)${PARTIAL_ZONED_WEEK_DATETIME_BASIC}$`);
const partialOrdinalDateTimeExtendedRegExp = new RegExp(R`^(?!$)${PARTIAL_ZONED_ORDINAL_DATETIME_EXTENDED}$`);
const partialOrdinalDateTimeBasicRegExp = new RegExp(R`^(?!$)${PARTIAL_ZONED_ORDINAL_DATETIME_BASIC}$`);

export class Interval<T extends Temporal<T>> implements Equatable, StructuralEquatable {
    readonly start: T;
    readonly end: T;

    constructor(start: T, end: T) {
        this.start = start.compareTo(end) <= 0 ? start : end;
        this.end = start.compareTo(end) <= 0 ? end : start;
    }

    get duration() {
        return this.start.until(this.end);
    }

    contains(value: T | Interval<T>) {
        if (value instanceof Interval) {
            return this.start.compareTo(value.start) <= 0
                && this.end.compareTo(value.end) >= 0;
        }
        return this.start.compareTo(value) <= 0
            && this.end.compareTo(value) >= 0;
    }

    overlaps(other: Interval<T>) {
        return this.contains(other.start)
            || this.contains(other.end)
            || other.contains(this.start)
            || other.contains(this.end);
    }

    withStartSameEnd(start: T) {
        return new Interval(start, this.end);
    }

    withStartSameDuration(start: T) {
        return new Interval(start, start.plus(this.duration));
    }

    withEndSameStart(end: T) {
        return new Interval(this.start, end);
    }

    withEndSameDuration(end: T) {
        return new Interval(end.minus(this.duration), end);
    }

    withDurationSameStart(duration: Duration) {
        return new Interval(this.start, this.start.plus(duration));
    }

    withDurationSameEnd(duration: Duration) {
        return new Interval(this.end.minus(duration), this.end);
    }

    equals(other: Interval<T>) {
        return this[Equatable.equals](other);
    }

    toStartEndIntervalString(): string {
        return `${this.start}/${this.end}`;
        // // write a consise representation of end
        // const startDate = getCivilDate(this.start);
        // const startTime = getCivilTime(this.start);
        // const startOffset = getOffset(this.start);
        // const endDate = getCivilDate(this.end);
        // const endTime = getCivilTime(this.end);
        // const endOffset = getOffset(this.end);

        // // determine components of date to write
        // let writeYear = false;
        // let writeMonth = false;
        // let writeDay = false;
        // let writeDate = false;
        // if (startDate && endDate) {
        //     writeYear = endDate.year !== startDate.year;
        //     writeMonth = writeYear || endDate.month !== startDate.month;
        //     writeDay = writeMonth || endDate.day !== startDate.day;
        //     writeDate = writeYear || writeMonth || writeDay;
        // }

        // // determine components of time to write
        // let writeOffset = false;
        // if (startOffset && endOffset) {
        //     writeOffset = !endOffset.equals(startOffset);
        // }

        // let writeNanosecond = false;
        // let writeMillisecond = false;
        // let writeSecond = false;
        // let writeMinute = false;
        // let writeHour = false;
        // let writeTime = false;
        // let writeTimeSeparator = false;
        // if (startTime && endTime) {
        //     writeNanosecond = endTime.nanosecond !== startTime.nanosecond;
        //     writeMillisecond = writeNanosecond || endTime.millisecond !== startTime.millisecond;
        //     writeSecond = writeMillisecond || endTime.second !== startTime.second;
        //     writeMinute = writeSecond || endTime.minute !== startTime.minute;
        //     writeHour = writeMinute || writeOffset || endTime.hour !== startTime.hour;
        //     if (writeHour) writeMinute = true;
        //     writeTime = writeHour || writeOffset;
        //     writeTimeSeparator = writeDate && writeTime || (writeHour && !writeMinute);
        // }

        // let s = "";
        // // if every component needs to be written, write it as an instant.
        // if (writeYear && writeMonth && writeDay && writeHour && writeMinute && writeSecond && writeOffset) {
        //     s = this.end.toISOString();
        // }
        // else {
        //     if (endDate) {
        //         if (writeYear) s += D4(endDate.year);
        //         if (writeYear && (writeMonth || writeDay)) s += "-";
        //         if (writeMonth) s += D2(endDate.month);
        //         if (writeMonth && writeDay) s += "-";
        //         if (writeDay) s += D2(endDate.day);
        //     }
        //     if (endTime) {
        //         if (writeTimeSeparator) s += "T";
        //         if (writeHour) s += D2(endTime.hour);
        //         if (writeHour && writeMinute) s += ":";
        //         if (writeMinute) s += D2(endTime.minute);
        //         if (writeMinute && writeSecond) s += ":";
        //         if (writeSecond) {
        //             let text = D2(endTime.second);
        //             if (writeMillisecond) {
        //                 text += `.${D3(endTime.millisecond)}`;
        //                 if (writeNanosecond) {
        //                     text += D6(endTime.nanosecond);
        //                 }
        //             }
        //             s += text;
        //         }
        //     }
        //     if (endOffset && writeOffset) {
        //         let offsetSeconds = endOffset.offsetSeconds;
        //         if (offsetSeconds === 0) {
        //             s += "Z";
        //         }
        //         else {
        //             let sign = "+";
        //             if (offsetSeconds < 0) {
        //                 sign = "-";
        //                 offsetSeconds = -offsetSeconds;
        //             }

        //             const h = Math.floor(offsetSeconds / 3600);
        //             const m = Math.floor(offsetSeconds / 60) % 60;
        //             const ss = offsetSeconds % 60;
        //             s += `${sign}${D2(h)}`;
        //             if (m !== 0 || ss !== 0) {
        //                 s += `:${D2(m)}`;
        //                 if (ss !== 0) {
        //                     s += `:${D2(ss)}`;
        //                 }
        //             }
        //         }
        //     }
        // }
        // return `${this.start.toString()}/${s}`;
    }

    toStartDurationIntervalString(): string {
        return `${this.start.toString()}/${this.duration.toString()}`;
    }

    toDurationEndIntervalString(): string {
        return `${this.duration.toString()}/${this.duration.toString()}`;
    }

    toString(): string {
        return this.toStartEndIntervalString();
    }

    toJSON() {
        return this.toString();
    }

    static fromStartDuration<T extends Temporal<T>>(start: T, duration: Duration) {
        return new Interval(start, start.plus(duration));
    }

    static fromDurationEnd<T extends Temporal<T>>(duration: Duration, end: T) {
        return new Interval(end.minus(duration), end);
    }

    static fromZonedDateTimeIntervalString(text: string) {
        return fromString(text, "ZonedDateTime", undefined);
    }

    static fromOffsetDateTimeIntervalString(text: string) {
        return fromString(text, "OffsetDateTime", undefined);
    }

    static fromCivilDateTimeIntervalString(text: string) {
        return fromString(text, "CivilDateTime", undefined);
    }

    static fromCivilDateIntervalString(text: string) {
        return fromString(text, "CivilDate", undefined);
    }

    static fromCivilTimeIntervalString(text: string) {
        return fromString(text, "CivilTime", undefined);
    }

    static fromOffsetTimeIntervalString(text: string) {
        return fromString(text, "OffsetTime", undefined);
    }

    static fromString(text: string) {
        return fromString(text, undefined, undefined);
    }

    [Equatable.equals](other: unknown): boolean {
        return this[StructuralEquatable.structuralEquals](other, Equaler.defaultEqualer);
    }

    [Equatable.hash](): number {
        return this[StructuralEquatable.structuralHash](Equaler.defaultEqualer);
    }

    [StructuralEquatable.structuralEquals](other: unknown, equaler: Equaler<unknown>): boolean {
        return other instanceof Interval
            && equaler.equals(this.start, other.start)
            && equaler.equals(this.end, other.end);
    }

    [StructuralEquatable.structuralHash](equaler: Equaler<unknown>): number {
        let hc = INTERVAL_HASH_SEED;
        hc = ((hc << 7) | (hc >>> 25)) ^ equaler.hash(this.start);
        hc = ((hc << 7) | (hc >>> 25)) ^ equaler.hash(this.end);
        return hc;
    }
}

function tryParseInt(x: string | undefined, fallback: number): number;
function tryParseInt(x: string | undefined, fallback?: number): number | undefined;
function tryParseInt(x: string | undefined, fallback?: number) {
    return x ? parseInt(x, 10) : fallback;
}

interface DateParts { year?: number, month?: number, day?: number }
interface TimeParts { hour?: number, minute?: number, second?: number, millisecond?: number, nanosecond?: number, offset?: string, ianaZone?: string }

function createFromPartialZonedDateTime(start: ZonedDateTime, date: DateParts, time: TimeParts) {
    const { year = start.year, month = start.month, day = start.day } = date;
    const { hour = start.hour, minute = start.minute, second = start.second, millisecond = start.millisecond, nanosecond = start.nanosecond } = time;
    return ZonedDateTime.fromCalendarDateTime(time.ianaZone || time.offset || start.zone, year, month, day, hour, minute, second, millisecond, nanosecond);
}

function createFromPartialOffsetDateTime(start: OffsetDateTime, date: DateParts, time: TimeParts) {
    const { year = start.year, month = start.month, day = start.day } = date;
    const { hour = start.hour, minute = start.minute, second = start.second, millisecond = start.millisecond, nanosecond = start.nanosecond } = time;
    return new OffsetDateTime(time.offset ? ZoneOffset.fromString(time.offset) : start.offset, year, month, day, hour, minute, second, millisecond, nanosecond);
}

function createFromPartialCivilDateTime(start: CivilDateTime, date: DateParts, time: TimeParts) {
    if (time.offset || time.ianaZone) return undefined;
    const { year = start.year, month = start.month, day = start.day } = date;
    const { hour = start.hour, minute = start.minute, second = start.second, millisecond = start.millisecond, nanosecond = start.nanosecond } = time;
    return new CivilDateTime(year, month, day, hour, minute, second, millisecond, nanosecond);
}

function createFromPartialCivilDate(start: CivilDate, date: DateParts, time: TimeParts) {
    if (time.offset || time.ianaZone || time.hour !== undefined || time.minute !== undefined || time.second !== undefined || time.millisecond !== undefined || time.nanosecond !== undefined) return undefined;
    const { year = start.year, month = start.month, day = start.day } = date;
    return new CivilDate(year, month, day);
}

function createFromPartialOffsetTime(start: OffsetTime, date: DateParts, time: TimeParts) {
    if (time.ianaZone || date.year !== undefined || date.month !== undefined || date.day !== undefined) return undefined;
    const { hour = start.hour, minute = start.minute, second = start.second, millisecond = start.millisecond, nanosecond = start.nanosecond } = time;
    return new OffsetTime(time.offset ? ZoneOffset.fromString(time.offset) : start.offset, hour, minute, second, millisecond, nanosecond);
}

function createFromPartialCivilTime(start: CivilTime, date: DateParts, time: TimeParts) {
    if (time.offset || time.ianaZone || date.year !== undefined || date.month !== undefined || date.day !== undefined) return undefined;
    const { hour = start.hour, minute = start.minute, second = start.second, millisecond = start.millisecond, nanosecond = start.nanosecond } = time;
    return new CivilTime(hour, minute, second, millisecond, nanosecond);
}

function parseTemporal(text: string, type?: "CivilDate" | "CivilTime" | "CivilDateTime" | "OffsetTime" | "OffsetDateTime" | "ZonedDateTime" | undefined) {
    if (type === undefined || type === "OffsetDateTime") {
        try { return OffsetDateTime.fromString(text); } catch { }
    }
    if (type === undefined || type === "OffsetTime") {
        try { return OffsetTime.fromString(text); } catch { }
    }
    if (type === undefined || type === "ZonedDateTime") {
        try { return ZonedDateTime.fromString(text); } catch { }
    }
    if (type === undefined || type === "CivilDateTime") {
        try { return CivilDateTime.fromString(text); } catch { }
    }
    if (type === undefined || type === "CivilDate") {
        try { return CivilDate.fromString(text); } catch { }
    }
    if (type === undefined || type === "CivilTime") {
        try { return CivilTime.fromString(text); } catch { }
    }
    throw new SyntaxError(`Invalid temporal string '${text}'.`);
}

function getOffset(temporal: Temporal) {
    return temporal instanceof ZonedDateTime ? temporal.zone.getOffset(temporal.instant) :
        temporal instanceof OffsetDateTime ? temporal.offset :
        temporal instanceof OffsetTime ? temporal.offset :
        undefined;
}

function getCivilDate(temporal: Temporal) {
    return temporal instanceof ZonedDateTime ? temporal.toCivilDate() :
        temporal instanceof OffsetDateTime ? temporal.toCivilDate() :
        temporal instanceof CivilDateTime ? temporal.toCivilDate() :
        temporal instanceof CivilDate ? temporal :
        undefined;
}

function getCivilTime(temporal: Temporal) {
    return temporal instanceof ZonedDateTime ? temporal.toCivilTime() :
        temporal instanceof OffsetDateTime ? temporal.toCivilTime() :
        temporal instanceof CivilDateTime ? temporal.toCivilTime() :
        temporal instanceof CivilTime ? temporal :
        temporal instanceof OffsetTime ? temporal.toCivilTime() :
        undefined;
}

function fromString(text: string, type: "CivilDate", kind: "start-end" | "start-duration" | "duration-end" | undefined): Interval<CivilDate>;
function fromString(text: string, type: "CivilTime", kind: "start-end" | "start-duration" | "duration-end" | undefined): Interval<CivilTime>;
function fromString(text: string, type: "CivilDateTime", kind: "start-end" | "start-duration" | "duration-end" | undefined): Interval<CivilDateTime>;
function fromString(text: string, type: "OffsetTime", kind: "start-end" | "start-duration" | "duration-end" | undefined): Interval<OffsetTime>;
function fromString(text: string, type: "OffsetDateTime", kind: "start-end" | "start-duration" | "duration-end" | undefined): Interval<OffsetDateTime>;
function fromString(text: string, type: "ZonedDateTime", kind: "start-end" | "start-duration" | "duration-end" | undefined): Interval<ZonedDateTime>;
function fromString(text: string, type: "CivilDate" | "CivilTime" | "CivilDateTime" | "OffsetTime" | "OffsetDateTime" | "ZonedDateTime" | undefined, kind: "start-end" | "start-duration" | "duration-end" | undefined): Interval<CivilDate | CivilTime | CivilDateTime | OffsetTime | OffsetDateTime | ZonedDateTime>;
function fromString(text: string, type: "CivilDate" | "CivilTime" | "CivilDateTime" | "OffsetTime" | "OffsetDateTime" | "ZonedDateTime" | undefined, kind: "start-end" | "start-duration" | "duration-end" | undefined) {
    const match = intervalRegExp.exec(text);
    if (!match) throw new SyntaxError(`Invalid interval string '${text}'.`);

    const { first, second } = match.groups!;

    let start: CivilDate | CivilTime | CivilDateTime | OffsetTime | OffsetDateTime | ZonedDateTime | undefined;
    try {
        if (first.startsWith("P")) {
            if (kind === "duration-end" && kind === undefined) {
                return Interval.fromDurationEnd(
                    Duration.fromString(first),
                    parseTemporal(second, type));
            }
        }
        else if (second.startsWith("P")) {
            if (kind === "start-duration" || kind === undefined) {
                return Interval.fromStartDuration(
                    parseTemporal(first, type),
                    Duration.fromString(second));
            }
        }
        else {
            start = parseTemporal(first);
        }
    }
    catch {
        throw new SyntaxError(`Invalid interval string '${text}'.`);
    }

    if (start === undefined || kind === "start-duration" || kind === "duration-end") {
        throw new SyntaxError(`Invalid interval string '${text}'.`);
    }

    let end: CivilDate | CivilTime | CivilDateTime | OffsetTime | OffsetDateTime | ZonedDateTime | undefined;
    try {
        end = parseTemporal(second, type);
    }
    catch {
        let date: DateParts;
        let groups: Record<string, string | undefined>;
        const calendar = partialCalendarDateTimeExtendedRegExp.exec(second) || partialCalendarDateTimeBasicRegExp.exec(second);
        if (calendar && calendar.groups) {
            groups = calendar.groups;
            date = {
                year: tryParseInt(groups.year),
                month: tryParseInt(groups.month),
                day: tryParseInt(groups.day)
            };
        }
        else {
            const week = partialWeekDateTimeExtendedRegExp.exec(second) || partialWeekDateTimeBasicRegExp.exec(second);
            if (week && week.groups && (start instanceof ZonedDateTime || start instanceof CivilDateTime || start instanceof CivilDate)) {
                groups = week.groups;
                let year = tryParseInt(groups.year, start.year);
                let weekOfYear = tryParseInt(groups.weekOfYear, start.weekOfYear);
                const dayOfWeek = tryParseInt(groups.dayOfWeek, start.dayOfWeek);
                while (weekOfYear < 1) year--, weekOfYear += getWeeksInYear(year);
                while (weekOfYear > getWeeksInYear(year)) weekOfYear -= getWeeksInYear(year), year++;
                date = getCalendarDateFromWeekDate(year, weekOfYear, dayOfWeek);;
            }
            else {
                const ordinal = partialOrdinalDateTimeExtendedRegExp.exec(second) || partialOrdinalDateTimeBasicRegExp.exec(second);
                if (ordinal && ordinal.groups && (start instanceof ZonedDateTime || start instanceof CivilDateTime || start instanceof CivilDate)) {
                    groups = ordinal.groups;
                    const year = tryParseInt(groups.year, start.year);
                    const dayOfYear = tryParseInt(groups.dayOfYear, start.dayOfYear);
                    date = getCalendarDateFromOrdinalDate(year, dayOfYear);
                }
                else {
                    throw new SyntaxError(`Invalid interval string '${text}'.`);
                }
            }
        }

        const time: TimeParts = {
            hour: tryParseInt(groups.hour),
            minute: tryParseInt(groups.minute),
            second: tryParseInt(groups.second),
            millisecond: groups.millisecond ? +groups.millisecond.padEnd(3, "0") : undefined,
            nanosecond: groups.nanosecond ? +groups.nanosecond.padEnd(6, "0") : undefined,
            offset: groups.offset,
            ianaZone: groups.ianaZone
        };

        if (start instanceof ZonedDateTime) {
            end = createFromPartialZonedDateTime(start, date, time);
        }
        else if (start instanceof OffsetDateTime) {
            end = createFromPartialOffsetDateTime(start, date, time);
        }
        else if (start instanceof CivilDateTime) {
            end = createFromPartialCivilDateTime(start, date, time);
        }
        else if (start instanceof CivilDate) {
            end = createFromPartialCivilDate(start, date, time);
        }
        else if (start instanceof OffsetTime) {
            end = createFromPartialOffsetTime(start, date, time);
        }
        else if (start instanceof CivilTime) {
            end = createFromPartialCivilTime(start, date, time);
        }
    }

    if (end === undefined) {
        throw new SyntaxError(`Invalid interval string '${text}'.`);
    }

    return new Interval(start, end);
}